import { Injectable, OnDestroy, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { BundleEntry, HealthcareService } from 'fhir/r4';
import { backendEndPointToken } from '../app.config';
import { TableFilters, TableFilteringService, TableRowModel } from '../shared/table-filtering.service';
import { StateService } from '../shared/state.service';
import { tap } from 'rxjs';

export interface HealthcareServiceRow extends TableRowModel {
    rawResource: HealthcareService;
    name: string;
    status: string;
    category?: string;
    location?: string;
    lastUpdated?: string;
}

@Injectable({ providedIn: 'root' })
export class HealthcareServiceSearchService implements OnDestroy {
    private readonly tableFiltering = inject<TableFilteringService<HealthcareService, HealthcareServiceRow>>(TableFilteringService);
    private readonly backendUrl = inject(backendEndPointToken);
    private readonly stateService = inject(StateService);

    private readonly internalDataSource = new MatTableDataSource<HealthcareServiceRow>();
    private pageSize = 25;

    readonly loading$ = this.tableFiltering.loading$;
    readonly total$ = this.tableFiltering.total$;
    readonly unknownTotal$ = this.tableFiltering.unknownTotal$;
    readonly error$ = this.tableFiltering.error$;

    init(options: { pageSize?: number } = {}): void {
        this.pageSize = options.pageSize ?? this.pageSize;

        this.tableFiltering.init({
            resourceType: 'HealthcareService',
            defaultQuery: `/HealthcareService?_count=${this.pageSize}&_sort=-_lastUpdated`,
            dataSource: this.internalDataSource,
            baseUrl: this.backendUrl,
            pageSize: this.pageSize,
            transformEntry: (resource, entry) => this.transformResource(resource, entry)
        });
    }

    get dataSource(): MatTableDataSource<HealthcareServiceRow> {
        return this.internalDataSource;
    }

    search(filters: TableFilters): void {
        // Map UI filter names to FHIR search parameters
        const fhirFilters: TableFilters = {};
        if (filters['name']) {
            fhirFilters['name:contains'] = filters['name'];
        }
        if (filters['status']) {
            fhirFilters['active'] = filters['status'] === 'active';
        }
        
        this.tableFiltering.search(fhirFilters);
    }

    changePage(pageIndex: number): void {
        this.tableFiltering.changePage(pageIndex);
    }

    refresh(): void {
        this.tableFiltering.refreshCurrentPage();
    }

    destroy(): void {
        this.tableFiltering.destroy();
        this.internalDataSource.disconnect();
        this.internalDataSource.data = [];
    }

    ngOnDestroy(): void {
        this.destroy();
    }

    private transformResource(resource: HealthcareService, entry?: BundleEntry<HealthcareService>): HealthcareServiceRow {
        const id = resource.id ?? entry?.resource?.id ?? `HealthcareService-${Date.now()}`;
        return {
            id,
            resourceType: 'HealthcareService',
            display: resource.name ?? 'Unnamed Service',
            rawResource: resource,
            name: resource.name ?? 'N/A',
            status: resource.active !== false ? 'active' : 'inactive',
            category: resource.category?.[0]?.text || resource.category?.[0]?.coding?.[0]?.display || 'N/A',
            location: resource.location?.[0]?.display || resource.location?.[0]?.reference || 'N/A',
            lastUpdated: resource.meta?.lastUpdated
        };
    }
}
